<template lang="pug">
.briq-form-element.resource-type-switch-autosuggest-container
  label {{ label }}
  briq-required-and-optional-markers(:isRequired="this.$attrs.required" :isDisabled="isDisabled" :hideOptionalMarker="hideOptionalMarker" :label="label")
  briq-tooltip(:help="help")
  .type-switch-holder
    span.caret-icon(:class="dropdownShown ? 'transform-chevron' : ''")
      BriqIcon(name="icon-input-caret-down-icon")
    vue-autosuggest(
      data-test-id="vue-autosuggest"
      v-model="query"
      v-bind="$attrs"
      :suggestions="filteredOptions"
      @selected="onSelected"
      :get-suggestion-value="getSuggestionValue"
      :input-props="inputProps"
      @focus="onFocus"
      @closed="onClose"
      :class="isDisabled ? `type-switch-autosuggest-input__disabled` : ``"
      )
      template(slot-scope="{ suggestion }")
        .suggestion-item-holder
          .type-switch-icon
            svg
              use(:xlink:href="`#${suggestion.item.icon}`")
          .resource-type-title-and-description-holder
            .resouce-type-title {{ suggestion.item.DisplayName }}
            .resouce-type-description {{ suggestion.item.Description }}
    .validation-message(v-if="error") {{ error }}

  TypeSwitchWarningModal.typeSwitchWarningModal(
    v-show="showTypeSwitchWarningModal"
    @cancel="cancelTypeSwitch" 
    @changeResourceType="changeResourceTypeFromModal"
    )
</template>

<script>
import TypeSwitchWarningModal from './TypeSwitchWarningModal.vue'
import { VueAutosuggest } from 'vue-autosuggest'
import { propEq } from 'ramda'
import ResourceType from '@v/inventory/resources/edit/ResourceType.js'

export default {
  name: 'ResourceTypeSwitchField',
  components: {
    VueAutosuggest,
    TypeSwitchWarningModal,
  },
  props: {
    label: String,
    name: {
      type: String,
      required: true,
    },
    hideOptionalMarker: {
      type: Boolean,
      default: false,
    },
    help: String,
    error: String,
    value: String,
    resourceTypes: {
      type: Array,
      required: true,
    },
    enableWarningModal: Boolean,
  },
  data() {
    return {
      query: '',
      selected: '',
      inputProps: {
        id: 'type-switch-autosuggest-input',
        class: 'type-switch-autosuggest-input',
        placeholder: 'Please choose resource type',
        disabled: this.$attrs.disabled,
      },
      showTypeSwitchWarningModal: false,
      temporarySelectedItem: {},
      dropdownShown: false,
    }
  },
  computed: {
    isDisabled() {
      return this.$attrs.disabled || this.$attrs.readonly
    },
    filteredOptions() {
      return [
        {
          data: this.resourceTypes.filter(option => {
            switch (option.ResourceType) {
              case ResourceType.Normal:
                option.icon = 'icon-normal-resource'
                break
              case ResourceType.SubResource:
                option.icon = 'icon-sub-resource'
                break
              case ResourceType.ResourceGroup:
                option.icon = 'icon-resource-group'
                break
              default:
                option.icon = 'test'
            }
            return (
              option.DisplayName.toLowerCase().indexOf(this.query.toLowerCase()) > -1 ||
              option.Description.toLowerCase().indexOf(this.query.toLowerCase()) > -1
            )
          }),
        },
      ]
    },
  },
  methods: {
    onSelected(item) {
      if (item.item.ResourceType === this.value) return
      if (this.enableWarningModal) this.showWarningModal(item)
      else this.changeResourceType(item)
    },
    showWarningModal(item) {
      this.temporarySelectedItem = item.item
      this.showTypeSwitchWarningModal = true
    },
    onFocus() {
      this.query = ''
      this.dropdownShown = true
    },
    onClose() {
      this.query = this.selected.DisplayName ? this.selected.DisplayName : ''
      this.dropdownShown = false
    },
    getSuggestionValue(suggestion) {
      return suggestion.item.DisplayName
    },
    cancelTypeSwitch() {
      this.showTypeSwitchWarningModal = false
    },
    changeResourceType(item) {
      this.selected = item.item
      this.query = this.selected.DisplayName ? this.selected.DisplayName : ''
      this.$emit('resourceTypeSelected', this.selected.ResourceType)
    },
    changeResourceTypeFromModal() {
      this.selected = this.temporarySelectedItem
      this.query = this.selected.DisplayName ? this.selected.DisplayName : ''
      this.$emit('resourceTypeSelected', this.selected.ResourceType)
      this.showTypeSwitchWarningModal = false
    },
  },
  mounted() {
    if (this.value !== null) {
      this.selected = this.resourceTypes.find(propEq('ResourceType', this.value))
      this.query = this.selected.DisplayName
    }
  },
}
</script>

<style lang="stylus">
@import '~@v/variables.styl'

.resource-type-switch-autosuggest-container
  width 100%
  position relative

  .type-switch-holder
    position relative

    .caret-icon
      position absolute
      right 13px
      top 9px
      z-index 2
      transform scale(0.75)
      transition all 0.2s ease;

    .transform-chevron
      transform rotate(-180deg) scale(0.75)

  #autosuggest
    display inline-block
    width 100%

  .type-switch-autosuggest-input
    {typeStandard}
    position: relative;
    width: 100%;
    border: solid 1px borderColor;
    border-radius: 3px;
    background: #fff;
    color: color-text;
    padding: 10px;

    &:focus, &:active
      outline 0
      box-shadow 0 2px 4px 0 rgba(0, 0, 0, .15)

  .type-switch-autosuggest-input__disabled
    input
      background-color disabledGrey
      cursor not-allowed

  .autosuggest__results-container
    width 100%
    position absolute
    z-index 1
    background white
    box-shadow 0 1px 4px rgba(0, 0, 0, 0.4)

    .autosuggest__results
      overflow-y auto
      max-height 400px

      ul
        list-style none
        padding-left 0
        margin 0

        li
          cursor pointer

          :hover
              background-color grey100

          .suggestion-item-holder
            display flex
            border-bottom 1px solid borderColor
            padding 10px

            .type-switch-icon
              padding 5px 18px 5px 5px

              svg
                width 35px
                height 35px

            .resource-type-title-and-description-holder
              padding-top 5px

              .resouce-type-title
                {typeBold}

              .resouce-type-description
                {typeSmall}
</style>
